import React, { useCallback, useEffect, useState } from 'react';
import { Check, Send, RefreshCw, X, Inbox, AlertTriangle } from 'lucide-react';
import { api, inputCls, Button, Notice, KIND_LABEL, timeAgo, formatWhen, describeTerms } from './ui';

/**
 * Drafts waiting for a person. Edits and notes are fed back to the agents as
 * lessons, so the same correction is not needed twice.
 */

const DraftCard = ({ draft, onDone, onOpen }) => {
  const [subject, setSubject] = useState(draft.subject || '');
  const [body, setBody] = useState(draft.body_text || '');
  const [note, setNote] = useState('');
  const [hint, setHint] = useState('');
  const [mode, setMode] = useState(null); // 'regenerate' | 'discard'
  const [skip, setSkip] = useState(false);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  const edited = body.trim() !== String(draft.body_text || '').trim() || subject !== (draft.subject || '');
  const run = async (key, fn) => {
    setBusy(key);
    setError(null);
    try {
      await fn();
      onDone();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(null);
    }
  };

  const approve = (sendNow) => run(sendNow ? 'send' : 'approve', () => api(`/messages/${draft.id}/approve`, {
    method: 'POST',
    body: { body: edited ? body : undefined, subject: subject !== draft.subject ? subject : undefined, note: note || undefined, send_now: sendNow }
  }));
  const regenerate = () => run('regenerate', () => api(`/messages/${draft.id}/regenerate`, { method: 'POST', body: { hint } }));
  const discard = () => run('discard', () => api(`/messages/${draft.id}/discard`, { method: 'POST', body: { note: note || undefined, skip_affiliate: skip } }));

  const inbound = draft.last_inbound;
  const terms = draft.deal_terms || {};
  const isNegotiation = ['reply', 'closing'].includes(draft.kind);

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700 dark:bg-slate-800 dark:text-slate-300">{KIND_LABEL[draft.kind] || draft.kind}</span>
        <button type="button" onClick={() => onOpen(draft.opportunity_id, draft.team_id)} className="font-semibold text-blue-700 hover:underline dark:text-blue-300">{draft.domain}</button>
        <span className="text-xs text-gray-500 dark:text-slate-400">to {draft.to_email} · {draft.team_name} · drafted {timeAgo(draft.created_at)}</span>
      </div>

      {draft.approval_reason && (
        <div className="mt-2 flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-300">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-none" /> {draft.approval_reason}
        </div>
      )}

      {isNegotiation && inbound && (
        <div className="mt-3 rounded-lg bg-gray-50 p-3 text-sm dark:bg-slate-950">
          <div className="mb-1 text-xs text-gray-500 dark:text-slate-400">They wrote ({inbound.from_email}, {formatWhen(inbound.received_at)}){inbound.intent ? ` · ${inbound.intent.replace(/_/g, ' ')}` : ''}</div>
          <div className="whitespace-pre-wrap text-gray-800 dark:text-slate-200">{inbound.body_text}</div>
        </div>
      )}
      {isNegotiation && (terms.their_ask || terms.our_offer) && (
        <div className="mt-2 flex flex-wrap gap-4 text-xs text-gray-600 dark:text-slate-400">
          {terms.their_ask && <span>They ask: <b className="text-gray-800 dark:text-slate-200">{describeTerms(terms.their_ask, draft.currency)}</b></span>}
          {terms.our_offer && <span>Our offer: <b className="text-gray-800 dark:text-slate-200">{describeTerms(terms.our_offer, draft.currency)}</b></span>}
        </div>
      )}
      {isNegotiation && draft.stage_note && <div className="mt-1 text-xs italic text-gray-500 dark:text-slate-400">Negotiator's plan: {draft.stage_note}</div>}

      <div className="mt-3 space-y-2">
        <input className={inputCls} value={subject} onChange={(e) => setSubject(e.target.value)} disabled={draft.kind !== 'first_touch'} />
        <textarea rows={Math.min(14, Math.max(5, body.split('\n').length + 1))} className={`${inputCls} font-sans`} value={body} onChange={(e) => setBody(e.target.value)} />
        <div className="text-xs text-gray-500 dark:text-slate-400">The sign-off and signature are added when it is sent. Send time: {formatWhen(draft.send_at)}.</div>
      </div>

      {mode === 'regenerate' && (
        <div className="mt-3 flex gap-2">
          <input className={inputCls} autoFocus placeholder="What should change? e.g. 'shorter, and ask which pages first'" value={hint} onChange={(e) => setHint(e.target.value)} />
          <Button variant="primary" icon={RefreshCw} busy={busy === 'regenerate'} disabled={!hint.trim()} onClick={regenerate}>Rewrite</Button>
        </div>
      )}
      {mode === 'discard' && (
        <div className="mt-3 space-y-2">
          <input className={inputCls} autoFocus placeholder="Why? (optional, the agent learns from it)" value={note} onChange={(e) => setNote(e.target.value)} />
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-slate-300">
            <input type="checkbox" checked={skip} onChange={(e) => setSkip(e.target.checked)} /> Also stop working this affiliate
          </label>
          <Button variant="danger" icon={X} busy={busy === 'discard'} onClick={discard}>Discard draft</Button>
        </div>
      )}
      {edited && mode === null && (
        <input className={`${inputCls} mt-2`} placeholder="What was wrong with the draft? (optional, becomes a lesson)" value={note} onChange={(e) => setNote(e.target.value)} />
      )}

      {error && <div className="mt-2"><Notice tone="error">{error}</Notice></div>}
      <div className="mt-3 flex flex-wrap gap-2">
        <Button variant="primary" icon={Check} busy={busy === 'approve'} onClick={() => approve(false)}>{edited ? 'Save and approve' : 'Approve'}</Button>
        <Button variant="success" icon={Send} busy={busy === 'send'} onClick={() => approve(true)}>Send now</Button>
        <Button icon={RefreshCw} onClick={() => setMode(mode === 'regenerate' ? null : 'regenerate')}>Regenerate</Button>
        <Button variant="danger" icon={X} onClick={() => setMode(mode === 'discard' ? null : 'discard')}>Discard</Button>
      </div>
    </div>
  );
};

const Approvals = ({ teamId, onChanged, onOpen }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allTeams, setAllTeams] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await api(`/approvals${!allTeams && teamId ? `?team_id=${teamId}` : ''}`);
      setRows(d.approvals || []);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [teamId, allTeams]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  const done = () => { load(); onChanged && onChanged(); };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600 dark:text-slate-400">Negotiation replies first. Approve sends at the planned time; Send now sends immediately.</p>
        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-slate-300">
          <input type="checkbox" checked={allTeams} onChange={(e) => setAllTeams(e.target.checked)} /> All teams
        </label>
      </div>
      {error && <Notice tone="error">{error}</Notice>}
      {!loading && !rows.length && (
        <div className="flex flex-col items-center rounded-xl border border-dashed border-gray-200 py-12 text-gray-500 dark:border-slate-800 dark:text-slate-400">
          <Inbox className="mb-2 h-8 w-8" />
          Nothing waiting for approval.
        </div>
      )}
      {rows.map((d) => <DraftCard key={d.id} draft={d} onDone={done} onOpen={onOpen} />)}
    </div>
  );
};

export default Approvals;
